import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, LogOut, RefreshCw, Inbox, MessageCircle, Mail, Phone } from "lucide-react";

const STATUS = ["new", "contacted", "confirmed", "done"];
const STATUS_STYLE = {
  new: "bg-accent/15 text-accent",
  contacted: "bg-primary/15 text-primary",
  confirmed: "bg-[#849386]/25 text-[#3f4a41]",
  done: "bg-muted text-muted-foreground",
};

const AdminDashboard = () => {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, auth);
      setBookings(data);
    } catch {
      toast.error("Could not load requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, auth);
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status updated.");
    } catch {
      toast.error("Update failed.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = STATUS.reduce((a, s) => ({ ...a, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground"><Heart className="w-5 h-5" /></span>
            <span className="font-serif text-xl font-semibold">MyySpace Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-sm text-muted-foreground mr-2">{user?.email}</span>
            <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="refresh-button"><RefreshCw className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={doLogout} data-testid="logout-button"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl">Session requests</h1>
            <p className="text-muted-foreground text-sm mt-1">{bookings.length} total · {counts.new || 0} new</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 rounded-xl bg-card" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All requests</SelectItem>
              {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : shown.length === 0 ? (
          <div className="rounded-3xl bg-card border border-border p-16 text-center" data-testid="empty-state">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No requests here yet.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {shown.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-3xl bg-card border border-border p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-serif text-xl">{b.full_name}</h3>
                      <Badge className={`capitalize border-0 ${STATUS_STYLE[b.status]}`}>{b.status}</Badge>
                      {b.under_18_consent && <Badge variant="outline" className="border-accent text-accent">Under-18 consent</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {b.email}</span>
                      <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {b.contact_number}</span>
                      <span>{b.session_mode}</span>
                    </div>
                    <p className="text-sm mt-2"><span className="text-muted-foreground">Preferred:</span> {b.preferred_day}, {b.preferred_time}</p>
                    {b.message && <p className="text-sm mt-2 bg-muted rounded-xl p-3 text-foreground/80">{b.message}</p>}
                    <p className="text-xs text-muted-foreground/70 mt-2">{new Date(b.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <a href={`https://wa.me/91${(b.contact_number || "").replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#20bd5a] w-full" data-testid={`whatsapp-${b.id}`}>
                        <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                      </Button>
                    </a>
                    <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                      <SelectTrigger className="w-36 rounded-full bg-background" data-testid={`status-select-${b.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
